import React from 'react'

import MUIDialog from '@mui/material/Dialog'
import { useTheme } from '@mui/material/styles'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import MUIDialogTitle from '@mui/material/DialogTitle'
import useMediaQuery from '@mui/material/useMediaQuery'
import MUIDialogContent from '@mui/material/DialogContent'
import MUIDialogActions from '@mui/material/DialogActions'
import type { DialogProps as MuiDialogProps } from '@mui/material/Dialog'

import { Button, LoadingBtn } from '../Button'

type DialogButtonProps = {
	text: string
	onClick?: () => Promise<void> | void
	loading?: boolean
	disabled?: boolean
	color?:
		| 'primary'
		| 'secondary'
		| 'error'
		| 'info'
		| 'success'
		| 'warning'
}

export type DialogProps = {
	title?: string
	onClose?: () => void
	showActions?: boolean
	submitButtonProps?: DialogButtonProps
	closeButtonProps?: Omit<DialogButtonProps, 'loading'>
} & MuiDialogProps

const Dialog = ({
	title,
	onClose,
	children,
	showActions,
	closeButtonProps,
	submitButtonProps,
	...rest
}: DialogProps) => {
	const theme = useTheme()
	const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))

	const hasActionButtons = !!closeButtonProps || !!submitButtonProps
	const shouldShowActions = showActions && hasActionButtons
	const disableCloseButton =
		hasActionButtons &&
		(closeButtonProps?.disabled || submitButtonProps?.loading)

	return (
		<MUIDialog fullScreen={fullScreen} onClose={onClose} {...rest}>
			{title && (
				<MUIDialogTitle className="flex justify-between items-center">
					{title}{' '}
					{onClose && (
						<IconButton
							onClick={onClose}
							disabled={disableCloseButton}
						>
							<CloseIcon />
						</IconButton>
					)}
				</MUIDialogTitle>
			)}
			<MUIDialogContent dividers>{children}</MUIDialogContent>
			{shouldShowActions && (
				<MUIDialogActions className="ml-5 mr-5">
					{closeButtonProps && (
						<Button {...closeButtonProps} variant="outlined" />
					)}
					{submitButtonProps && (
						<LoadingBtn fullWidth={false} {...submitButtonProps} />
					)}
				</MUIDialogActions>
			)}
		</MUIDialog>
	)
}

export default Dialog
